import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TrailerCard = ({ trailer, viewMode = 'grid' }) => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(`/trailer-detail/${trailer?.id}`);
  };

  const renderRatingStars = (rating) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name={star <= rating ? 'Star' : 'Star'}
            size={16}
            strokeWidth={2}
            className={star <= rating ? 'text-accent fill-accent' : 'text-muted-foreground'}
          />
        ))}
      </div>
    );
  };

  // Map database fields to component props
  const displayImage = trailer?.photos?.[0] || '/assets/images/no_image.png';
  const displayName = trailer?.title || 'Trailer';
  const displayDescription = trailer?.description || 'No description available';
  const displayType = trailer?.trailerType || 'Unknown';
  const displayDimensions = trailer?.lengthFeet && trailer?.widthFeet 
    ? `${trailer?.lengthFeet}' x ${trailer?.widthFeet}'` 
    : 'N/A';
  const displayCapacity = trailer?.weightCapacityLbs 
    ? `${trailer?.weightCapacityLbs} lbs` 
    : 'N/A';
  const displayLocation = trailer?.locationCity && trailer?.locationState
    ? `${trailer?.locationCity}, ${trailer?.locationState}`
    : trailer?.locationCity || trailer?.locationState || 'N/A';
  const displayRating = trailer?.averageRating || 0;
  const displayPrice = trailer?.dailyRate || 0;
  const isAvailable = trailer?.status === 'active';
  const distanceMiles = trailer?.distanceMiles;

  if (viewMode === 'list') {
    return (
      <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden hover:shadow-elevation-3 transition-smooth">
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-80 h-48 md:h-auto overflow-hidden flex-shrink-0">
            <Image
              src={displayImage}
              alt={`${displayName} - ${displayType} trailer`}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex-1 p-4 md:p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
                    {displayName}
                  </h3>
                  <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                    {distanceMiles !== undefined && (
                      <span className="flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                        <Icon name="Navigation" size={12} strokeWidth={2} />
                        <span>{distanceMiles} mi</span>
                      </span>
                    )}
                    {isAvailable && (
                      <span className="flex items-center space-x-1 px-3 py-1 bg-success/10 text-success rounded-full text-sm font-medium whitespace-nowrap">
                        <Icon name="CheckCircle" size={14} strokeWidth={2} />
                        <span>Available</span>
                      </span>
                    )}
                  </div>
                </div>

                <p className="font-body text-muted-foreground mb-3 line-clamp-2">
                  {displayDescription}
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Package" size={18} strokeWidth={2} className="text-muted-foreground" />
                    <span className="font-body text-sm text-foreground">{displayType}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Ruler" size={18} strokeWidth={2} className="text-muted-foreground" />
                    <span className="font-body text-sm text-foreground">{displayDimensions}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Weight" size={18} strokeWidth={2} className="text-muted-foreground" />
                    <span className="font-body text-sm text-foreground">{displayCapacity}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="MapPin" size={18} strokeWidth={2} className="text-muted-foreground" />
                    <span className="font-body text-sm text-foreground">{displayLocation}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    {renderRatingStars(displayRating)}
                    <span className="font-body text-sm text-foreground font-medium">
                      {displayRating?.toFixed(1)}
                    </span>
                    <span className="font-caption text-sm text-muted-foreground">
                      ({trailer?.bookingCount || 0} bookings)
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-end space-y-3 md:ml-6">
                <div className="text-right">
                  <div className="font-heading text-3xl font-bold text-primary">
                    ${displayPrice}
                  </div>
                  <div className="font-caption text-sm text-muted-foreground">per day</div>
                </div>

                <Button
                  variant="default"
                  size="lg"
                  onClick={handleViewDetails}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="w-full md:w-auto"
                >
                  View Details
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 overflow-hidden hover:shadow-elevation-3 transition-smooth h-full flex flex-col">
      <div className="relative w-full h-48 md:h-56 overflow-hidden flex-shrink-0">
        <Image
          src={displayImage}
          alt={`${displayName} - ${displayType} trailer`}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 right-3 flex items-center space-x-2">
          {distanceMiles !== undefined && (
            <span className="flex items-center space-x-1 px-2 py-1 bg-primary text-primary-foreground rounded-full text-xs font-medium shadow-elevation-2">
              <Icon name="Navigation" size={12} strokeWidth={2} />
              <span>{distanceMiles} mi</span>
            </span>
          )}
          {isAvailable && (
            <span className="flex items-center space-x-1 px-3 py-1 bg-success text-success-foreground rounded-full text-sm font-medium shadow-elevation-2">
              <Icon name="CheckCircle" size={14} strokeWidth={2} />
              <span>Available</span>
            </span>
          )}
        </div>
      </div>
      <div className="p-4 md:p-5 flex-1 flex flex-col">
        <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground mb-2 line-clamp-1">
          {displayName}
        </h3>

        <p className="font-body text-sm text-muted-foreground mb-3 line-clamp-2 flex-grow">
          {displayDescription}
        </p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Package" size={16} strokeWidth={2} className="text-muted-foreground" />
            <span className="font-body text-sm text-foreground">{displayType}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Ruler" size={16} strokeWidth={2} className="text-muted-foreground" />
            <span className="font-body text-sm text-foreground">{displayDimensions}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Weight" size={16} strokeWidth={2} className="text-muted-foreground" />
            <span className="font-body text-sm text-foreground">{displayCapacity}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="MapPin" size={16} strokeWidth={2} className="text-muted-foreground" />
            <span className="font-body text-sm text-foreground">{displayLocation}</span>
          </div>
        </div>

        <div className="flex items-center space-x-2 mb-4">
          {renderRatingStars(displayRating)}
          <span className="font-body text-sm text-foreground font-medium">
            {displayRating?.toFixed(1)}
          </span>
          <span className="font-caption text-sm text-muted-foreground">
            ({trailer?.bookingCount || 0})
          </span>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
          <div>
            <div className="font-heading text-2xl font-bold text-primary">
              ${displayPrice}
            </div>
            <div className="font-caption text-xs text-muted-foreground">per day</div>
          </div>

          <Button
            variant="default"
            size="default"
            onClick={handleViewDetails}
            iconName="ArrowRight"
            iconPosition="right"
          >
            View Details
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TrailerCard;